import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { recordImpressionByName } from '@JohnLewisPartnership/experiments-utils';
import { getTopLinksWithMenu } from '../links';
import { Prenav } from '../ui/prenav/Prenav';
import { Logo } from '../logo/Logo';
import { UserTools } from '../user-tools/UserTools';
import { Search } from '../search/Search';
import {
  isMin1280pxWide,
  isMin768pxWide,
} from '../../utils/device/identifyDevice';
import { isMonetateGroup } from '../../utils/monetate/isMonetateGroup';
import { EnvVarsFeaturesContext } from '../../scaffold/EnvVarsFeaturesContext';
import { UserContext } from '../../scaffold/UserContext';
import { useHeaderVisibility } from './hooks/useHeaderVisibility';
import { useMobileMenuToggle } from './hooks/useMobileMenuToggle';
import { useSearchFocus } from './hooks/useSearchFocus';

import styles from './responsive-header.scss';
import { TypeaheadOverlay } from '../typeahead-overlay/TypeaheadOverlay';
import { CloseButton } from '../typeahead-overlay/CloseButton';

interface ResponsiveHeaderProps {
  updateMobileNavState: (isOpen: boolean) => void;
  updateTypeaheadState: (isOpen: boolean) => void;
  children?: React.ReactNode;
}

export const ResponsiveHeader = ({
  updateMobileNavState,
  updateTypeaheadState,
  children,
}: ResponsiveHeaderProps) => {
  const {
    features,
    envVars,
    stickySearchHeader: hasStickyHeader,
  } = useContext(EnvVarsFeaturesContext) as EnvVarsFeaturesContextValue;

  const {
    userState: {
      user: { monetateId },
    },
  } = useContext(UserContext);

  const [isTypeaheadOpen, setIsTypeaheadOpen] = useState(false);

  const { onlyPeepAtTop = false } = features;

  const monetate = envVars.monetate;

  const header = useRef(null);
  const headerContainer = useRef(null);

  const shouldRecordImpression = isMonetateGroup(features, 'record-impression');

  const topLinksData = getTopLinksWithMenu();

  const { showNav, isClosingMenu, onToggleMenu } = useMobileMenuToggle({
    updateMobileNavState,
  });
  const isMenuVisibleOrClosing = showNav || isClosingMenu;

  const handleCloseMenuEvent = useCallback(() => {
    onToggleMenu();
  }, [onToggleMenu]);

  const dispatchPeepEvent = useCallback((peeped: boolean) => {
    const event = new CustomEvent('jl-events.browser.mobileHeaderPeepUpdated', {
      detail: { peeped },
    });

    window.dispatchEvent(event);
  }, []);

  const dispatchStickyHeaderEvent = useCallback((peeped: boolean) => {
    const event = new CustomEvent('jl-events.browser.stickyHeaderPeepUpdated', {
      detail: { peeped },
    });

    window.dispatchEvent(event);
  }, []);

  const {
    isVisible,
    stickyHeaderActive,
    handleUnpeep,
    forceStickyHeaderInactive,
  } = useHeaderVisibility({
    hasStickyHeader,
    isFocused: isTypeaheadOpen,
    onlyPeepAtTop,
    showNav: isMenuVisibleOrClosing,
    dispatchPeepEvent,
    dispatchStickyHeaderEvent,
  });

  const { searchInputBlur, searchInputFocus, onSearchTabOut } = useSearchFocus({
    hasStickyHeader,
    isFocused: isTypeaheadOpen,
    setIsFocused: setIsTypeaheadOpen,
    monetate,
    monetateExperiences: features.monetateExperiences,
    monetateId,
    showNav,
    stickyHeaderActive,
    typeaheadOpenClassName: styles.typeaheadOpen,
    updateTypeaheadState,
  });

  const handleSearchInputBlur = useCallback(() => {
    searchInputBlur();

    if (isMin768pxWide()) {
      forceStickyHeaderInactive();
    }
  }, [forceStickyHeaderInactive, searchInputBlur]);

  const handlePreNavFocus = () => {
    if (stickyHeaderActive) window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(
    function recordHeaderImpression() {
      if (shouldRecordImpression && monetateId) {
        recordImpressionByName(
          monetate?.channel,
          features.monetateExperiences as unknown as Parameters<
            typeof recordImpressionByName
          >[1],
          'record-impression',
          monetateId
        );
      }
    },
    [
      shouldRecordImpression,
      monetate?.channel,
      features.monetateExperiences,
      monetateId,
    ]
  );

  useEffect(
    function listenForMobileHeaderEvents() {
      window.addEventListener(
        'jl-events.browser.mobileHeaderCloseMenu',
        handleCloseMenuEvent
      );
      window.addEventListener(
        'jl-events.browser.mobileHeaderUnpeep',
        handleUnpeep
      );

      return function stopListeningForMobileHeaderEvents() {
        window.removeEventListener(
          'jl-events.browser.mobileHeaderCloseMenu',
          handleCloseMenuEvent
        );
        window.removeEventListener(
          'jl-events.browser.mobileHeaderUnpeep',
          handleUnpeep
        );
      };
    },
    [handleCloseMenuEvent]
  );

  useEffect(
    function syncStickyHeaderClass() {
      if (stickyHeaderActive) {
        document.documentElement.classList.add('jl-sticky-header-active');
      } else {
        document.documentElement.classList.remove('jl-sticky-header-active');
      }
    },
    [stickyHeaderActive]
  );

  const handleOverlayKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        onToggleMenu(event);
      }
    },
    [onToggleMenu]
  );

  const wrapperClasses = classNames(styles.header, {
    [styles.navOpenPersistent]: isMenuVisibleOrClosing,
  });

  const overlayClasses = classNames(styles.overlay, {
    [styles.showOverlay]: showNav,
  });

  const splashClasses = isTypeaheadOpen
    ? classNames(styles.expandedSearch)
    : classNames(styles.search, {
        [styles.search__shiftPosition]: !stickyHeaderActive,
      });

  const containerClasses = classNames(styles.container, {
    [styles.peepHeader]: features.peepHeader,
    [styles.peep]: features.peepHeader && !isVisible && !isMenuVisibleOrClosing,
    [styles.hasStickyHeader]: hasStickyHeader,
  });

  const typeaheadSeparatorClasses = classNames(styles.typeaheadSeparator, {
    [styles.typeaheadSeparator__hidden]: !isTypeaheadOpen,
    [styles.typeaheadSeparator__hidden__doTransition]: !isTypeaheadOpen,
  });
  return (
    <div
      className={wrapperClasses}
      ref={headerContainer}
      data-testid="header-wrapper"
    >
      <div
        className={containerClasses}
        ref={header}
        data-testid="header-container"
      >
        <Prenav
          isFocused={isTypeaheadOpen}
          isStickyHeaderActive={stickyHeaderActive}
          onFocus={isTypeaheadOpen ? handleSearchInputBlur : handlePreNavFocus}
          topLinksData={topLinksData}
        />
        <div className={styles.wrapper}>
          <div className={styles.pageContainer}>
            <div className={styles.grid}>
              <div className={styles.logoRowContainer}>
                <div
                  className={classNames(styles.logoRow, {
                    [styles.logoRow__active]: isTypeaheadOpen,
                  })}
                  data-testid="header-logo-row"
                >
                  <Logo splashScreen={isTypeaheadOpen} />
                  <div
                    className={splashClasses}
                    id="mobile-search"
                    data-testid="mobile-search"
                  >
                    <Search
                      id="mobileSearch"
                      onFocus={searchInputFocus}
                      isTypeaheadOpen={isTypeaheadOpen}
                      onTabOut={onSearchTabOut}
                    />
                  </div>

                  <div className={styles.overlayButtonsContainer}>
                    <div className={styles.overlayButtons}>
                      <ul>
                        <li>
                          <CloseButton
                            isTypeaheadOpen={isTypeaheadOpen}
                            onCloseButtonClick={() => {
                              handleSearchInputBlur();
                              updateTypeaheadState(false);
                              onToggleMenu(null, true);
                            }}
                          />
                        </li>
                      </ul>
                    </div>

                    <UserTools
                      isMenuOpen={showNav}
                      onMenuButtonClick={e => onToggleMenu(e)}
                      isTypeaheadOpen={isTypeaheadOpen}
                    />
                  </div>
                </div>
                <TypeaheadOverlay
                  isTypeaheadOpen={isTypeaheadOpen}
                  onClickAway={handleSearchInputBlur}
                />
                <hr
                  data-testid="typeahead-separator"
                  className={typeaheadSeparatorClasses}
                />
              </div>
            </div>
          </div>
        </div>
        {(!stickyHeaderActive || !isMin1280pxWide()) && children}
      </div>

      <div
        className={overlayClasses}
        onClick={e => onToggleMenu(e)}
        role="button"
        tabIndex={-1}
        onKeyUp={handleOverlayKeyPress}
      />
    </div>
  );
};
